import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CreditCard, Loader2, CheckCircle, Sparkles, Gift } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/hooks/useAuth";
import { useSubscriptionContext } from "@/contexts/SubscriptionContext";
import { useTrialEligibility } from "@/hooks/useTrialEligibility";
import { supabase } from "@/integrations/supabase/client";
import { trackEvent, getBillingPlatform } from "@/lib/analytics";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useIsNativeMobile, useIsNativeIOS, NativePremiumMessage } from "@/hooks/useIsNativeIOS";
import { isAppleIapEnabled } from "@/lib/iap/feature-flag";
import { IOSPricingPlanSelector } from "@/components/IOSPricingPlanSelector";

type PlanKey = "monthly" | "quarterly" | "annual";

interface Plan {
  key: PlanKey;
  labelEs: string;
  labelEn: string;
  price: string;
  periodEs: string;
  periodEn: string;
  savingKey?: "save56" | "save11";
  highlight?: boolean;
  badgeKey?: "bestValue" | "mostFlexible";
  subTextKey?: "equivalentMonthly";
}

// NOTE: Visual order only. Price IDs live in the edge function `create-checkout`
// keyed by `plan` ('monthly' | 'quarterly' | 'annual'). Do NOT change these keys.
const PLANS: Plan[] = [
  {
    key: "monthly",
    labelEs: "Mensual",
    labelEn: "Monthly",
    price: "$14.99",
    periodEs: "/mes",
    periodEn: "/month",
  },
  {
    key: "quarterly",
    labelEs: "Trimestral",
    labelEn: "3 Months",
    price: "$39.99",
    periodEs: "/3 meses",
    periodEn: "/3 months",
    savingKey: "save11",
    badgeKey: "mostFlexible",
  },
  {
    key: "annual",
    labelEs: "Anual",
    labelEn: "Yearly",
    price: "$79.99",
    periodEs: "/año",
    periodEn: "/year",
    savingKey: "save56",
    highlight: true,
    badgeKey: "bestValue",
    subTextKey: "equivalentMonthly",
  },
];

interface PricingPlanSelectorProps {
  variant?: "full" | "compact";
  className?: string;
}

export function PricingPlanSelector({ variant = "full", className }: PricingPlanSelectorProps) {
  const { language, t } = useLanguage();
  const { user } = useAuth();
  const { revalidate } = useSubscriptionContext();
  const { eligible: trialEligible } = useTrialEligibility();
  const isNativeMobile = useIsNativeMobile();
  const isNativeIOS = useIsNativeIOS();
  const appleIapEnabled = isAppleIapEnabled();
  // Default to annual to highlight best value (visual/conversion only — Price ID
  // is resolved server-side in `create-checkout` from this plan key).
  const [selectedPlan, setSelectedPlan] = useState<PlanKey>("annual");
  const [isLoading, setIsLoading] = useState(false);
  const isEs = language === "es";

  // Checkout abandonment: Stripe sends the user back here with
  // ?checkout=cancelled when they close the hosted checkout page.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") !== "cancelled") return;
    trackEvent("checkout_abandoned", "pricing_plan_selector", { provider: "stripe" });
    params.delete("checkout");
    const qs = params.toString();
    window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
  }, []);

  // Native mobile (iOS + Android): hide Stripe entirely to comply with
  // App Store and Google Play external-payment policies. Existing premium
  // access (active subs, has_free_access, institutional) is unaffected — it is
  // resolved server-side and read via SubscriptionContext.
  if (isNativeMobile) {
    // Native iOS with the Apple IAP flag ON renders the real StoreKit
    // selector (localized Apple prices, in-app purchase, Restore Purchases).
    // In every other native case (Android, iOS with flag OFF) we keep the
    // existing NativePremiumMessage exactly as-is.
    if (isNativeIOS && appleIapEnabled) {
      return (
        <div className={cn("space-y-4", className)}>
          <IOSPricingPlanSelector />
        </div>
      );
    }
    return (
      <div className={cn("space-y-4", className)}>
        <NativePremiumMessage language={language} />
      </div>
    );
  }


  const handleCheckout = async () => {
    if (!user) {
      toast.error(isEs ? "Debes iniciar sesión" : "You must be logged in");
      return;
    }

    // Hard runtime guard: Stripe must never be reached from the native iOS
    // shell (App Store guideline 3.1.1). The component already returns early
    // on native, but this protects against any future call site.
    const billingPlatform = getBillingPlatform();
    if (billingPlatform === "ios_native") {
      console.warn("[PricingPlanSelector] Stripe checkout blocked on native iOS");
      return;
    }

    setIsLoading(true);
    // checkout_started = the Stripe checkout session is actually being created
    // (web / android PWA only — this code path never runs on native).
    trackEvent("checkout_started", "pricing_plan_selector", { plan: selectedPlan, provider: "stripe" });
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { userId: user.id, plan: selectedPlan, platform: billingPlatform },
      });

      // 409 already_subscribed: server detected an active Stripe subscription
      // and refused to create a duplicate checkout. Re-sync access and inform.
      if (error) {
        let parsed: any = null;
        try { parsed = await (error as any).context?.json?.(); } catch { /* noop */ }
        if (parsed?.error === "already_subscribed") {
          toast.success(
            isEs
              ? "Ya tienes una suscripción activa. Tu acceso fue sincronizado."
              : "You already have an active subscription. Your access has been synced."
          );
          await revalidate();
          return;
        }
        throw error;
      }
      if (data?.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error("Checkout error:", error);
      toast.error(isEs ? "Error al procesar el pago" : "Payment processing error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={cn("space-y-4", className)}>
      {trialEligible && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-primary/30 bg-gradient-to-r from-primary/10 to-accent/10 px-3 py-2 text-center">
          <Gift className="w-4 h-4 text-primary shrink-0" />
          <p className="text-xs sm:text-sm font-semibold text-foreground">
            {isEs
              ? "Prueba gratis por 7 días · cancela cuando quieras"
              : "7-day free trial · cancel anytime"}
          </p>
        </div>
      )}
      <div className={cn("grid gap-3", variant === "full" ? "grid-cols-1 sm:grid-cols-3" : "grid-cols-3")}>
        {PLANS.map((plan) => {
          const isSelected = selectedPlan === plan.key;
          const label = isEs ? plan.labelEs : plan.labelEn;
          const period = isEs ? plan.periodEs : plan.periodEn;
          const saving = plan.savingKey ? t(`plan.${plan.savingKey}`) : null;
          const badge = plan.badgeKey ? t(`plan.${plan.badgeKey}`) : null;
          const subText = plan.subTextKey ? t(`plan.${plan.subTextKey}`) : null;

          return (
            <Card
              key={plan.key}
              onClick={() => {
                setSelectedPlan(plan.key);
                trackEvent("plan_selected", "pricing_plan_selector", {
                  plan: plan.key,
                  provider: "stripe",
                });
              }}
              className={cn(
                "relative cursor-pointer p-4 pt-5 transition-all duration-200 text-center",
                isSelected
                  ? "border-2 border-primary bg-primary/5 shadow-md"
                  : "border border-border hover:border-primary/40",
                plan.highlight && !isSelected && "border-primary/50 ring-1 ring-primary/20"
              )}
            >
              {badge && (
                <span
                  className={cn(
                    "absolute -top-2 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full whitespace-nowrap",
                    plan.highlight
                      ? "bg-gradient-to-r from-primary to-accent text-primary-foreground shadow-sm"
                      : "bg-muted text-muted-foreground border border-border"
                  )}
                >
                  {plan.highlight && <Sparkles className="w-2.5 h-2.5" />}
                  {badge}
                </span>
              )}
              {isSelected && (
                <CheckCircle className="absolute top-2 right-2 w-4 h-4 text-primary" />
              )}
              <div className="mb-1.5 flex flex-col items-center gap-1">
                <p className="text-xs font-medium text-muted-foreground">{label}</p>
              </div>
              <p className="text-lg sm:text-xl font-bold text-foreground">{plan.price}</p>
              <p className="text-[10px] text-muted-foreground">{period}</p>
              {trialEligible && (
                <p className="mt-1 text-[10px] font-semibold text-primary">
                  {isEs ? "Gratis los primeros 7 días" : "Free for 7 days"}
                </p>
              )}
              {saving && (
                <span
                  className={cn(
                    "inline-block mt-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full",
                    plan.highlight
                      ? "text-green-700 dark:text-green-300 bg-green-100 dark:bg-green-900/40"
                      : "text-green-600 dark:text-green-400 bg-green-100/70 dark:bg-green-900/30"
                  )}
                >
                  {saving}
                </span>
              )}
              {subText && (
                <p className="mt-1.5 text-[10px] font-medium text-primary">{subText}</p>
              )}
            </Card>
          );
        })}
      </div>

      <Button
        onClick={handleCheckout}
        disabled={isLoading}
        size="lg"
        className="w-full gap-2 bg-gradient-to-r from-primary to-accent hover:from-primary/90 hover:to-accent/90 text-primary-foreground font-bold"
      >
        {isLoading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : (
          <CreditCard className="w-5 h-5" />
        )}
        {trialEligible
          ? (isEs ? "Empezar prueba gratis de 7 días" : "Start 7-day free trial")
          : (isEs ? "Activar plan" : "Activate plan")}
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        {trialEligible
          ? (isEs
              ? "Acceso completo durante 7 días. $0 hoy; al terminar la prueba comienza el plan elegido. Cancela cuando quieras."
              : "Full access for 7 days. $0 today; your selected plan starts when the trial ends. Cancel anytime.")
          : (isEs ? "Cancela cuando quieras. Sin contratos." : "Cancel anytime. No contracts.")}
      </p>
    </div>
  );
}

